import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { Theme, ThemeId } from './types'
import { THEMES, DEFAULT_THEME, getThemeById } from './themes'

interface ThemeContextType {
  currentTheme: Theme
  setTheme: (themeId: ThemeId | string) => void
  themes: Theme[]
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const STORAGE_KEY = 'nexus-theme'

function applyThemeToDOM(theme: Theme) {
  const root = document.documentElement
  const { colors } = theme

  root.style.setProperty('--bg-main', colors.bgMain)
  root.style.setProperty('--bg-card', colors.bgCard)
  root.style.setProperty('--bg-element', colors.bgElement)
  root.style.setProperty('--bg-hover', colors.bgHover)
  root.style.setProperty('--border', colors.border)
  root.style.setProperty('--text-main', colors.textMain)
  root.style.setProperty('--text-muted', colors.textMuted)
  root.style.setProperty('--primary', colors.primary)
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [currentTheme, setCurrentTheme] = useState<Theme>(() => {
    // Try to load from localStorage on initial render
    if (typeof window !== 'undefined') {
      const savedThemeId = localStorage.getItem(STORAGE_KEY)
      if (savedThemeId) {
        return getThemeById(savedThemeId)
      }
    }
    return DEFAULT_THEME
  })

  // Apply theme to DOM whenever it changes
  useEffect(() => {
    applyThemeToDOM(currentTheme)
  }, [currentTheme])

  const setTheme = useCallback((themeId: ThemeId | string) => {
    const theme = getThemeById(themeId)
    setCurrentTheme(theme)
    localStorage.setItem(STORAGE_KEY, theme.id)
  }, [])

  return (
    <ThemeContext.Provider
      value={{
        currentTheme,
        setTheme,
        themes: THEMES,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
